import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { Avatar } from '@app/components/core/avatar/Avatar'
import { Banner } from '@app/components/core/banner/Banner'
import { Text } from '@app/components/core/typography/Text'
import { Button } from '@app/components/core/button/Button'
import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useEffect, useState } from 'react'
import { match } from 'ts-pattern'
import { TextInput } from '@app/components/core/input/text/TextInput'
import { EditButton } from '@app/components/core/button/EditButton'

export type MainInfoEditPanelProps = {
  name: string
  email: string
  banner: string
  avatar: string
  onSave: (
    name?: string,
    email?: string,
    avatar?: File,
    banner?: File,
  ) => Promise<boolean>
}

export const MainInfoEditPanel = ({
  name,
  email,
  avatar,
  banner,
  onSave,
}: MainInfoEditPanelProps) => {
  const translations = useTranslations().app.common
  const profileTranslations = useTranslations().app.dashboard.profile
  const [editMode, setEditMode] = useState(false)

  const [newAvatar, setNewAvatar] = useState<{ preview: string; file: File }>()
  const [newBanner, setNewBanner] = useState<{ preview: string; file: File }>()
  const [newName, setNewName] = useState<string>(name)

  const reset = useCallback(() => {
    if (newAvatar?.preview) {
      URL.revokeObjectURL(newAvatar.preview)
    }
    if (newBanner?.preview) {
      URL.revokeObjectURL(newBanner.preview)
    }
    setNewAvatar(null)
    setNewBanner(null)
    setNewName(name)
    setEditMode(false)
  }, [newAvatar, newBanner, name])

  const save = useCallback(() => {
    void onSave(newName, null, newAvatar?.file, newBanner?.file).then(
      (success) => {
        if (success) {
          reset()
        }
      },
    )
  }, [newAvatar, newBanner, newName, onSave, reset])

  useEffect(() => setNewName(name), [name])

  const isNameValid = useCallback(() => {
    if (newName.trim().length < 4) {
      return false
    }
    if (!newName.trim().includes(' ')) {
      return false
    }

    return true
  }, [newName])

  return (
    <Column
      alignItems="stretch"
      style={{
        backgroundColor: 'var(--shades-white)',
        borderRadius: '1.25rem',
        overflow: 'clip',
      }}
    >
      <Banner
        image={newBanner ? newBanner.preview : banner}
        editMode={editMode}
        onNewBannerSeleted={(file: File) =>
          setNewBanner({ preview: URL.createObjectURL(file), file })
        }
        bannerStyle={{ borderRadius: 0, height: '11rem' }}
      >
        <Avatar
          size={'large'}
          variant="pink"
          labelSource={name}
          editMode={editMode}
          image={newAvatar ? newAvatar.preview : avatar}
          onNewAvatarSeleted={(file: File) =>
            setNewAvatar({ preview: URL.createObjectURL(file), file })
          }
        />
      </Banner>
      <Column
        alignItems="stretch"
        gap={'0.5rem'}
        style={{
          padding: '0 1.5rem 1.5rem 1.5rem',
        }}
      >
        {match(editMode)
          .with(true, () => (
            <Row
              style={{
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: '0.5rem',
              }}
            >
              <TextInput
                label={profileTranslations.fullName}
                displayLabelAsterisk={true}
                text={newName}
                errorMessage={
                  isNameValid() ? null : profileTranslations.nameErrorMessage
                }
                setText={setNewName}
                style={{
                  flex: '1 1 auto',
                }}
              />
            </Row>
          ))
          .otherwise(() => (
            <>
              <Row
                style={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: '0.5rem',
                }}
              >
                <Title.H4 style={{ marginBottom: '0' }}>{name}</Title.H4>
                <EditButton
                  isVisible={!editMode}
                  onClick={() => setEditMode(true)}
                />
              </Row>
              <Text.Medium color="var(--gray-300)">{email}</Text.Medium>
            </>
          ))}
        {editMode && (
          <Row style={{ justifyContent: 'flex-end', paddingTop: '1.5rem' }}>
            <Button
              variant="secondary"
              label={translations.cancel}
              onClick={reset}
            />
            <Button
              variant="primary"
              label={translations.save}
              disabled={!isNameValid()}
              onClick={save}
            />
          </Row>
        )}
      </Column>
    </Column>
  )
}
